import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { config } from "../config";

interface Position {
  id: string;
  bot_id: string;
  base_qty: number;
  quote_spent: number;
  avg_price: number;
  updated_at: number;
}

function Positions() {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiBase}/positions`);
      if (response.ok) {
        const data = await response.json();
        setPositions(data);
      } else {
        setError('Failed to fetch positions. Please ensure your exchange connection is working and try again.');
      }
    } catch (err) {
      setError('Failed to fetch positions. Please ensure your exchange connection is working and try again.');
      console.error('Positions error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Trading Positions</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">⚠️ {error}</p>
          </div>
        )}

        {/* Positions Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-blue-600 text-2xl">💰</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-600">Total Positions</p>
                <p className="text-2xl font-bold text-blue-900">{positions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 text-2xl">📊</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-600">Total Value</p>
                <p className="text-2xl font-bold text-green-900">
                  ${positions.reduce((sum, pos) => sum + pos.quote_spent, 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-purple-600 text-2xl">⚖️</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-600">Avg Price</p>
                <p className="text-2xl font-bold text-purple-900">
                  ${positions.length > 0 
                    ? (positions.reduce((sum, pos) => sum + pos.avg_price, 0) / positions.length).toFixed(2)
                    : '0.00'
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-orange-600 text-2xl">🔄</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-600">Last Updated</p>
                <p className="text-lg font-bold text-orange-900">
                  {positions.length > 0 
                    ? new Date(Math.max(...positions.map(p => p.updated_at))).toLocaleDateString()
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Positions List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bot ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote Spent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {positions.map((position) => (
                <tr key={position.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {position.id.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {position.bot_id.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {position.base_qty.toFixed(6)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${position.quote_spent.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${position.avg_price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(position.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {positions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No positions found. Trading bots will create positions when they execute trades.
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
    </>
  );
}

export default Positions;







